import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircleIcon,
  UserCircleIcon,
  CurrencyDollarIcon,
  CreditCardIcon,
  DocumentTextIcon,
  SparklesIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';

interface OnboardingStep {
  id: number;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  completed: boolean;
}

const CreatorOnboardingWizard: React.FC = () => {
  const navigate = useNavigate();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [steps, setSteps] = useState<OnboardingStep[]>([
    {
      id: 1,
      title: 'Complete Your Profile',
      description: 'Set up your creator profile with bio, avatar, and social links',
      icon: UserCircleIcon,
      completed: false,
    },
    {
      id: 2,
      title: 'Create Membership Tiers',
      description: 'Define your subscription tiers with pricing and benefits',
      icon: CurrencyDollarIcon,
      completed: false,
    },
    {
      id: 3,
      title: 'Connect Stripe',
      description: 'Set up your payout account to receive payments',
      icon: CreditCardIcon,
      completed: false,
    },
    {
      id: 4,
      title: 'Create Your First Post',
      description: 'Share content with your future patrons',
      icon: DocumentTextIcon,
      completed: false,
    },
  ]);

  // Load progress from localStorage
  useEffect(() => {
    const savedProgress = localStorage.getItem('onboarding_progress');
    if (savedProgress) {
      try {
        const progress = JSON.parse(savedProgress);
        setCurrentStep(progress.currentStep || 1);
        setSteps(progress.steps || steps);
      } catch (error) {
        console.error('Failed to load onboarding progress:', error);
      }
    }
  }, []);

  // Save progress to localStorage
  useEffect(() => {
    const progress = {
      currentStep,
      steps,
      updatedAt: new Date().toISOString(),
    };
    localStorage.setItem('onboarding_progress', JSON.stringify(progress));
  }, [currentStep, steps]);

  const markStepComplete = (stepId: number) => {
    setSteps(steps.map(step => 
      step.id === stepId ? { ...step, completed: true } : step
    ));
  };

  const goToNextStep = () => {
    if (currentStep < steps.length) {
      markStepComplete(currentStep);
      setCurrentStep(currentStep + 1);
    }
  };

  const goToPreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const skipOnboarding = () => {
    localStorage.setItem('onboarding_skipped', 'true');
    navigate('/dashboard');
  };

  const completeOnboarding = () => {
    markStepComplete(currentStep);
    localStorage.setItem('onboarding_completed', 'true');
    localStorage.removeItem('onboarding_progress');
    navigate('/dashboard');
  };

  const currentStepData = steps.find(s => s.id === currentStep);
  const progress = (steps.filter(s => s.completed).length / steps.length) * 100;

  return (
    <div 
      className="min-h-screen flex items-center justify-center px-4 py-12"
      style={{ backgroundColor: 'var(--patreon-cream)' }}
    >
      <div className="max-w-4xl w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4" style={{ background: 'linear-gradient(135deg, var(--patreon-orange), #ff8c69)' }}>
            <SparklesIcon className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Welcome to Your Creator Journey!
          </h1>
          <p className="text-lg text-gray-600">
            Let's get you set up in just a few steps
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">
              Step {currentStep} of {steps.length}
            </span>
            <span className="text-sm font-medium" style={{ color: 'var(--patreon-orange)' }}>
              {Math.round(progress)}% Complete
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div
              className="h-2.5 rounded-full transition-all duration-300"
              style={{
                width: `${progress}%`,
                background: 'linear-gradient(90deg, var(--patreon-orange), #ff8c69)',
              }}
            />
          </div>
        </div>

        {/* Steps Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {steps.map((step) => {
            const Icon = step.icon;
            const isCurrent = step.id === currentStep;
            const isCompleted = step.completed;
            
            return (
              <button
                key={step.id}
                onClick={() => setCurrentStep(step.id)}
                className={`
                  relative p-4 rounded-xl border-2 transition-all duration-200
                  ${isCurrent 
                    ? 'border-[color:var(--patreon-orange)] bg-orange-50' 
                    : isCompleted
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                  }
                `}
              >
                {isCompleted && (
                  <div className="absolute top-2 right-2">
                    <CheckCircleIcon className="w-5 h-5 text-green-600" />
                  </div>
                )}
                <Icon className={`w-8 h-8 mx-auto mb-2 ${
                  isCurrent 
                    ? 'text-[color:var(--patreon-orange)]' 
                    : isCompleted
                    ? 'text-green-600'
                    : 'text-gray-400'
                }`} />
                <p className={`text-xs font-medium ${
                  isCurrent 
                    ? 'text-[color:var(--patreon-orange)]' 
                    : isCompleted
                    ? 'text-green-700'
                    : 'text-gray-600'
                }`}>
                  {step.title}
                </p>
              </button>
            );
          })}
        </div>

        {/* Current Step Content */}
        <div
          className="rounded-2xl p-8 shadow-lg mb-6"
          style={{
            backgroundColor: 'var(--patreon-card-bg)',
            border: '1px solid var(--patreon-card-border)',
          }}
        >
          {currentStepData && (
            <>
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  {currentStepData.title}
                </h2>
                <p className="text-gray-600">
                  {currentStepData.description}
                </p>
              </div>

              {/* Step-specific content */}
              <div className="space-y-6">
                {currentStep === 1 && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-4">What you'll do:</h3>
                    <ul className="space-y-3">
                      <li className="flex items-start">
                        <CheckCircleIcon className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">Add a profile picture and banner image</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircleIcon className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">Write a compelling bio about your work</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircleIcon className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">Connect your social media accounts</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircleIcon className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">Customize your creator page colors</span>
                      </li>
                    </ul>
                    <button
                      onClick={() => {
                        navigate('/dashboard/creator/profile');
                        markStepComplete(1);
                      }}
                      className="w-full mt-6 flex items-center justify-center px-6 py-3 rounded-full text-white font-semibold transition-all duration-200 shadow-md hover:shadow-lg"
                      style={{ backgroundColor: 'var(--patreon-btn-primary-bg)' }}
                    >
                      Go to Profile Settings
                      <ArrowRightIcon className="w-5 h-5 ml-2" />
                    </button>
                  </div>
                )}

                {currentStep === 2 && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-4">What you'll do:</h3>
                    <ul className="space-y-3">
                      <li className="flex items-start">
                        <CheckCircleIcon className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">Create at least one membership tier</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircleIcon className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">Set pricing and billing cycle (monthly/yearly)</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircleIcon className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">List the benefits patrons will receive</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircleIcon className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">Make your tiers visible to the public</span>
                      </li>
                    </ul>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
                      <p className="text-sm text-blue-800">
                        <strong>💡 Tip:</strong> Most creators start with 2-3 tiers at different price points ($5, $10, $25). You can always add more later!
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        navigate('/dashboard/creator/tiers');
                        markStepComplete(2);
                      }}
                      className="w-full mt-6 flex items-center justify-center px-6 py-3 rounded-full text-white font-semibold transition-all duration-200 shadow-md hover:shadow-lg"
                      style={{ backgroundColor: 'var(--patreon-btn-primary-bg)' }}
                    >
                      Create Membership Tiers
                      <ArrowRightIcon className="w-5 h-5 ml-2" />
                    </button>
                  </div>
                )}

                {currentStep === 3 && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-4">What you'll do:</h3>
                    <ul className="space-y-3">
                      <li className="flex items-start">
                        <CheckCircleIcon className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">Connect your Stripe account (80% of earnings)</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircleIcon className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">Verify your identity with Stripe</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircleIcon className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">Set up your payout method (bank account)</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircleIcon className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">Complete Stripe's onboarding process</span>
                      </li>
                    </ul>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-6">
                      <p className="text-sm text-yellow-800">
                        <strong>⚠️ Important:</strong> You'll need your tax ID and bank account information. The platform takes a 20% fee to cover payment processing and platform costs.
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        navigate('/dashboard/creator/payouts');
                        markStepComplete(3);
                      }}
                      className="w-full mt-6 flex items-center justify-center px-6 py-3 rounded-full text-white font-semibold transition-all duration-200 shadow-md hover:shadow-lg"
                      style={{ backgroundColor: 'var(--patreon-btn-primary-bg)' }}
                    >
                      Connect Stripe Account
                      <ArrowRightIcon className="w-5 h-5 ml-2" />
                    </button>
                  </div>
                )}

                {currentStep === 4 && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-4">What you'll do:</h3>
                    <ul className="space-y-3">
                      <li className="flex items-start">
                        <CheckCircleIcon className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">Share your first piece of content</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircleIcon className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">Choose which tiers can access it</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircleIcon className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">Add images, videos, or polls</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircleIcon className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">Publish or schedule for later</span>
                      </li>
                    </ul>
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mt-6">
                      <p className="text-sm text-purple-800">
                        <strong>✨ Pro tip:</strong> Your first post can be a welcome message, a sneak peek of your work, or exclusive behind-the-scenes content!
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        navigate('/dashboard/creator/posts');
                        markStepComplete(4);
                      }}
                      className="w-full mt-6 flex items-center justify-center px-6 py-3 rounded-full text-white font-semibold transition-all duration-200 shadow-md hover:shadow-lg"
                      style={{ backgroundColor: 'var(--patreon-btn-primary-bg)' }}
                    >
                      Create Your First Post
                      <ArrowRightIcon className="w-5 h-5 ml-2" />
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between">
          <div>
            {currentStep > 1 && (
              <button
                onClick={goToPreviousStep}
                className="flex items-center px-6 py-3 rounded-full font-semibold transition-all duration-200 border-2"
                style={{
                  borderColor: 'var(--patreon-orange)',
                  color: 'var(--patreon-orange)',
                }}
              >
                <ArrowLeftIcon className="w-5 h-5 mr-2" />
                Previous
              </button>
            )}
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={skipOnboarding}
              className="px-6 py-3 rounded-full font-semibold text-gray-600 hover:text-gray-800 transition-all duration-200"
            >
              Skip for Now
            </button>

            {currentStep < steps.length ? (
              <button
                onClick={goToNextStep}
                className="flex items-center px-6 py-3 rounded-full text-white font-semibold transition-all duration-200 shadow-md hover:shadow-lg"
                style={{ backgroundColor: 'var(--patreon-btn-primary-bg)' }}
              >
                Next Step
                <ArrowRightIcon className="w-5 h-5 ml-2" />
              </button>
            ) : (
              <button
                onClick={completeOnboarding}
                className="flex items-center px-6 py-3 rounded-full text-white font-semibold transition-all duration-200 shadow-md hover:shadow-lg"
                style={{ backgroundColor: 'var(--patreon-btn-primary-bg)' }}
              >
                Finish Onboarding
                <CheckCircleIcon className="w-5 h-5 ml-2" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreatorOnboardingWizard;
